import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Mail,
  Lock,
  KeyRound,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  Eye,
  EyeOff,
  ShieldCheck,
  Send,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { cognitoForgotPassword, cognitoConfirmForgotPassword } from '../lib/cognito-auth';
import { toast } from 'sonner';

interface ForgotPasswordPageProps {
  onNavigate: (page: string) => void;
}

type Step = 'request' | 'confirm' | 'success' | 'unavailable';

export function ForgotPasswordPage({ onNavigate }: ForgotPasswordPageProps) {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFeatureUnavailable, setIsFeatureUnavailable] = useState(false);

  // Password validation
  const passwordRequirements = [
    { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
    { label: 'Contains uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
    { label: 'Contains lowercase letter', test: (p: string) => /[a-z]/.test(p) },
    { label: 'Contains a number', test: (p: string) => /\d/.test(p) },
    { label: 'Contains special character', test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
  ];

  const isPasswordValid = passwordRequirements.every((r) => r.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  // Step 1: Request reset code
  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await cognitoForgotPassword(email);
      setStep('confirm');
      toast.success('Reset code sent!', {
        description: `Check your inbox at ${email}`,
      });
    } catch (err: any) {
      logger.error('Forgot password error', err as Error, undefined, 'ForgotPasswordPage');
      // Detect "endpoint not deployed" error from the API layer
      if (err.isEndpointMissing) {
        setIsFeatureUnavailable(true);
        setStep('unavailable');
        return;
      }
      setError(err.message || 'Failed to send reset code. Please check your email and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Confirm with code + new password
  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError('Password does not meet the requirements.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      await cognitoConfirmForgotPassword(email, code, newPassword);
      setStep('success');
      toast.success('Password reset successfully!', {
        description: 'You can now sign in with your new password',
      });
    } catch (err: any) {
      logger.error('Confirm forgot password error', err as Error, undefined, 'ForgotPasswordPage');
      setError(err.message || 'Failed to reset password. Please check your code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend the code (re-triggers forgot password)
  const handleResendCode = async () => {
    setError('');
    try {
      await cognitoForgotPassword(email);
      toast.success('New reset code sent!', {
        description: `Check your inbox at ${email}`,
      });
    } catch (err: any) {
      toast.error('Failed to resend code', {
        description: err.message || 'Please try again',
      });
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="border-border/50 bg-card/95 backdrop-blur-xl shadow-2xl border-2 border-[#FDB022]/20">
          <CardHeader className="text-center space-y-2">
            <AnimatePresence mode="wait">
              {step === 'request' && (
                <motion.div key="request-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                    className="mx-auto h-16 w-16 rounded-full bg-[#FDB022]/10 flex items-center justify-center"
                  >
                    <KeyRound className="h-8 w-8 text-[#FDB022]" />
                  </motion.div>
                  <CardTitle className="text-2xl font-bold">Forgot Password?</CardTitle>
                  <CardDescription>
                    Enter your email address and we'll send you a code to reset your password
                  </CardDescription>
                </motion.div>
              )}

              {step === 'confirm' && (
                <motion.div key="confirm-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="mx-auto h-16 w-16 rounded-full bg-[#FDB022]/10 flex items-center justify-center"
                  >
                    <Lock className="h-8 w-8 text-[#FDB022]" />
                  </motion.div>
                  <CardTitle className="text-2xl font-bold">Reset Your Password</CardTitle>
                  <CardDescription>
                    Enter the code sent to <span className="font-medium text-foreground">{email}</span> and your new password
                  </CardDescription>
                </motion.div>
              )}

              {step === 'success' && (
                <motion.div key="success-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="mx-auto h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center"
                  >
                    <ShieldCheck className="h-8 w-8 text-green-500" />
                  </motion.div>
                  <CardTitle className="text-2xl font-bold text-green-500">Password Reset!</CardTitle>
                  <CardDescription>
                    Your password has been reset successfully. You can now sign in with your new password.
                  </CardDescription>
                </motion.div>
              )}

              {step === 'unavailable' && (
                <motion.div key="unavailable-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="mx-auto h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center"
                  >
                    <ShieldCheck className="h-8 w-8 text-red-500" />
                  </motion.div>
                  <CardTitle className="text-2xl font-bold text-red-500">Feature Unavailable</CardTitle>
                  <CardDescription>
                    The password reset feature is currently unavailable. Please try again later.
                  </CardDescription>
                </motion.div>
              )}
            </AnimatePresence>
          </CardHeader>

          <CardContent className="space-y-4">
            <AnimatePresence mode="wait">
              {/* ===================== STEP 1: REQUEST CODE ===================== */}
              {step === 'request' && (
                <motion.div
                  key="request-step"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleRequestCode} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="forgot-email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); setError(''); }}
                          className="pl-10"
                          required
                          autoFocus
                          autoComplete="email"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
                      disabled={isLoading || !email}
                      data-testid="forgot-password-send-code"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending Code...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Send Reset Code
                        </>
                      )}
                    </Button>
                  </form>

                  <button
                    type="button"
                    onClick={() => onNavigate('home')}
                    className="flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors pt-1"
                    data-testid="forgot-password-back-to-home"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back to Home
                  </button>
                </motion.div>
              )}

              {/* ===================== STEP 2: ENTER CODE + NEW PASSWORD ===================== */}
              {step === 'confirm' && (
                <motion.div
                  key="confirm-step"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div className="rounded-lg bg-[#FDB022]/10 border border-[#FDB022]/30 p-3">
                    <p className="text-sm text-[#FDB022]">
                      A reset code has been sent to your email. Enter it below along with your new password.
                    </p>
                  </div>

                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleConfirmReset} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset-code">Reset Code</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="reset-code"
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={code}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setCode(val);
                          }}
                          className="pl-10 text-center text-lg tracking-[0.5em] font-mono"
                          required
                          maxLength={6}
                          autoFocus
                          autoComplete="one-time-code"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="new-password"
                          type={showNewPassword ? 'text' : 'password'}
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                          className="pl-10 pr-10"
                          required
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          data-testid="forgot-password-toggle-new-password-visibility"
                        >
                          {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>

                      {/* Password requirements */}
                      {newPassword.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="space-y-1 pt-1"
                        >
                          {passwordRequirements.map((req) => {
                            const passes = req.test(newPassword);
                            return (
                              <div
                                key={req.label}
                                className={`flex items-center gap-1.5 text-xs transition-colors ${
                                  passes ? 'text-green-500' : 'text-muted-foreground'
                                }`}
                              >
                                <CheckCircle2 className={`w-3 h-3 ${passes ? 'text-green-500' : 'text-muted-foreground/40'}`} />
                                {req.label}
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="confirm-new-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm new password"
                          value={confirmPassword}
                          onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                          className="pl-10 pr-10"
                          required
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          data-testid="forgot-password-toggle-confirm-password-visibility"
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {confirmPassword.length > 0 && !passwordsMatch && (
                        <p className="text-xs text-red-400">Passwords do not match</p>
                      )}
                      {passwordsMatch && (
                        <p className="text-xs text-green-500 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Passwords match
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
                      disabled={isLoading || code.length < 6 || !isPasswordValid || !passwordsMatch}
                      data-testid="forgot-password-reset-submit"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Resetting Password...
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="mr-2 h-4 w-4" />
                          Reset Password
                        </>
                      )}
                    </Button>
                  </form>

                  {/* Resend code */}
                  <div className="flex items-center justify-center gap-1 text-sm">
                    <span className="text-muted-foreground">Didn't receive the code?</span>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      className="text-[#FDB022] hover:underline font-medium"
                      data-testid="forgot-password-resend-code"
                    >
                      Resend Code
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => { setStep('request'); setError(''); setCode(''); setNewPassword(''); setConfirmPassword(''); }}
                    className="flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors pt-1"
                    data-testid="forgot-password-change-email"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Change Email
                  </button>
                </motion.div>
              )}

              {/* ===================== STEP 3: SUCCESS ===================== */}
              {step === 'success' && (
                <motion.div
                  key="success-step"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4 text-center"
                >
                  <div className="rounded-lg bg-green-500/10 border border-green-500/30 p-4">
                    <p className="text-sm text-green-400">
                      Your password has been successfully reset. You can now use your new password to sign in.
                    </p>
                  </div>

                  <Button
                    onClick={() => onNavigate('home')}
                    className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
                    data-testid="forgot-password-go-to-signin"
                  >
                    Go to Sign In
                  </Button>
                </motion.div>
              )}

              {/* ===================== STEP 4: FEATURE UNAVAILABLE ===================== */}
              {step === 'unavailable' && (
                <motion.div
                  key="unavailable-step"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-4 space-y-2">
                    <p className="text-sm text-red-400 font-medium">
                      Password reset is temporarily unavailable.
                    </p>
                    <p className="text-xs text-red-400/70">
                      There was an issue connecting to the password reset service. Please try again later
                      or contact your administrator for assistance.
                    </p>
                  </div>

                  <div className="rounded-lg bg-zinc-800/60 border border-zinc-700/50 p-4 space-y-3">
                    <p className="text-sm font-medium text-white">What you can do instead:</p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Mail className="w-4 h-4 mt-0.5 text-[#FDB022] flex-shrink-0" />
                        <span>Contact your administrator to reset your password manually.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <KeyRound className="w-4 h-4 mt-0.5 text-[#FDB022] flex-shrink-0" />
                        <span>If you're a test user, try password: <code className="text-[#FDB022] bg-zinc-800 px-1 rounded">Admin123!</code> (admin) or <code className="text-[#FDB022] bg-zinc-800 px-1 rounded">TempPassword123!</code> (others)</span>
                      </li>
                    </ul>
                  </div>

                  <Button
                    onClick={() => onNavigate('home')}
                    className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
                    data-testid="forgot-password-back-to-signin"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Sign In
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>All authentication is managed through AWS Cognito</p>
        </div>
      </motion.div>
    </div>
  );
}